import { lazy } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, Route } from 'react-router-dom';
import { PaymentResult } from '~/components/payment/async.js';
import { WelcomeScreen, ResponsibleGamingLimits } from '~/components/account/async.js';
import AccountGateway from '~/components/account/account-gateway.js';
import { CloseContext } from '~/hooks/use-close-context.js';
import ExternalIframeRequest from '~/components/external-iframe-request.js';
import customDesktopScroll from '~/components/scroll/custom-desktop-scroll.js';
import useConfiguration from '~/configuration/use-configuration.js';
import {
	selectHasProducts,
	selectDisableUsernamePasswordLogin,
	selectOntarioFranchise,
	selectIsRegistrationBlocked,
	selectOneBankIDFlowEnabled,
	selectOneButtonFlow
} from '~/components/brand/ducks/selectors.js';
import { TermsConditionsBonus, CookiePolicy } from '~/components/pages/pages-async.js';
import Support from '~/components/support-async.js';
import ShopPointsFaq from '~/components/shop/shop-points-faq-async.ts';
import TermsAndConditions from '~/components/pages/terms-and-conditions.js';
import PrivacyPolicy from '~/components/pages/privacy-policy-configured.js';
import {
	TournamentLobbyLoader,
	TournamentResultsLoader,
	EndedTournamentListLoader
} from '~/components/casino-shared/tournaments/async';
import {
	ROUTE_AUTH_RES_GAMING,
	ROUTE_RESET_PASSWORD,
	ROUTE_RESET_PASSWORD_EMAIL,
	ROUTE_RESET_PASSWORD_PHONE,
	ROUTE_RESET_PASSWORD_CODE,
	ROUTE_RESET_PASSWORD_DOB_VERIFICATION,
	ROUTE_RESET_PASSWORD_WELCOME,
	ROUTE_NEW_PASSWORD,
	ROUTE_EXTERNAL_LOGIN,
	ROUTE_EXTERNAL_REQUEST,
	ROUTE_ACCOUNT,
	ROUTE_WELCOME,
	ROUTE_SUPPORT,
	ROUTE_POINTS_FAQ,
	ROUTE_TERMS_CONDITIONS_SIDEBAR,
	ROUTE_TERMS_CONDITIONS_BONUS_SIDEBAR,
	ROUTE_PRIVACY_POLICY_SIDEBAR,
	ROUTE_LOGIN,
	ROUTE_AUTHENTICATION,
	ROUTE_REGISTER,
	ROUTE_PLAY_NOW,
	ROUTE_LOGIN_GUEST,
	ROUTE_PLAY_AS_GUEST,
	ROUTE_PAYMENT_RESULT,
	ROUTE_GUEST_DEPOSIT_LOGIN,
	ROUTE_GUEST_DEPOSIT_LOGIN_RESULT,
	ROUTE_GUEST_INSTANT_DEPOSIT,
	ROUTE_GUEST_LOGIN_VERIFY_ACCOUNT,
	ROUTE_COOKIE_POLICY_SIDEBAR
} from '~/components/router/constants.js';
import { ROUTE_WE_SPIN_GAME_ROOM_ABS as ROUTE_WE_SPIN_NEWFLOW_GAME_ROOM_ABS } from '~/components/we-spin/new-flow/constants/we-spin-route-constants.js';
import { ROUTE_WE_SPIN_GAME_ROOM_ABS as ROUTE_WE_SPIN_OLDFLOW_GAME_ROOM_ABS } from '~/components/we-spin/old-flow/constants/we-spin-route-constants.js';
import {
	ROUTE_TOURNAMENT_LOBBY,
	ROUTE_TOURNAMENT_RESULTS,
	ROUTE_ENDED_TOURNAMENTS
} from '~/components/casino-shared/tournaments/routes';
import ProtectedRoute from '~/components/router/protected-route';
import ContextAwareRoutes from '~/components/router/context-aware-routes';
import Loading from '~/components/misc/loading';
import { Login } from '~/components/authentication/async';
import PlaynowGateway from '~/components/authentication/playnow-gateway';
import Register from '~/components/authentication/register/register';
import { selectBankIdEnabled } from '~/components/brand/ducks';
import WeSpinNewFlowGameRoomRoutes from '~/components/we-spin/new-flow/game-room/we-spin-game-room-routes';
import { WeSpinGameRoomSidebarController as WeSpinOldFlowGameRoomRoutes } from '~/components/we-spin/old-flow/index.js';
import { selectConfiguration } from '~/components/we-spin/old-flow/ducks/we-spin-selectors';
import SidebarCommon from '~/components/sidebar/sidebar-common';

const RESET_PASSWORD_ROUTES_MAP = {
	[ROUTE_RESET_PASSWORD]: lazy(() => import('~/components/authentication/reset-password/reset-password-start.js')),
	[ROUTE_RESET_PASSWORD_EMAIL]: lazy(() => import('~/components/authentication/reset-password/verify-email.js')),
	[ROUTE_RESET_PASSWORD_PHONE]: lazy(() => import('~/components/authentication/reset-password/verify-phone.js')),
	[ROUTE_RESET_PASSWORD_CODE]: lazy(() => import('~/components/authentication/reset-password/verify-code.js')),
	[ROUTE_RESET_PASSWORD_DOB_VERIFICATION]: lazy(() =>
		import('~/components/authentication/reset-password/reset-password-dob-verification.js')
	),
	[ROUTE_RESET_PASSWORD_WELCOME]: lazy(() =>
		import('~/components/authentication/reset-password/reset-password-welcome.js')
	),
	[ROUTE_NEW_PASSWORD]: lazy(() => import('~/components/authentication/reset-password/new-password.js'))
};

const GuestDepositLogin = lazy(() =>
	import(/* webpackChunkName: "guest-deposit-login" */ '~/components/guest-login/guest-deposit-login')
);
const GuestLogin = lazy(() => import(/* webpackChunkName: "guest-login" */ '~/components/guest-login/guest-login'));
const InstantDepositPage = lazy(() =>
	import(/* webpackChunkName: "instant-deposit-page" */ '~/components/guest-login/instant-deposit-page')
);
const ExternalLogin = lazy(() =>
	import(/* webpackChunkName: "external-login" */ '~/components/guest-login/external-login')
);

const GUEST_DEPOSIT_LOGIN_ROUTES_MAP = {
	[ROUTE_GUEST_DEPOSIT_LOGIN]: GuestDepositLogin,
	[ROUTE_GUEST_DEPOSIT_LOGIN_RESULT]: () => <GuestLogin flowDepositLogin />,
	[ROUTE_GUEST_INSTANT_DEPOSIT]: InstantDepositPage
};

const RoutesSideBar = () => {
	const {
		showLoginAsGuestOption = false,
		isAnonymous,
		useTrustlyLogin,
		hasUsernamePasswordAuthentication
	} = useConfiguration();
	const { weSpinNewFlowEnabled } = useSelector(selectConfiguration);
	const { hasWeSpin } = useSelector(selectHasProducts);
	const disableUsernamePasswordLogin = useSelector(selectDisableUsernamePasswordLogin);
	const oneBankIDFlowEnabled = useSelector(selectOneBankIDFlowEnabled);
	const oneButtonFlow = useSelector(selectOneButtonFlow);
	const hasAuthResGaming = useSelector(selectOntarioFranchise);
	const isRegistrationBlocked = useSelector(selectIsRegistrationBlocked);
	const bankIdEnabled = useSelector(selectBankIdEnabled);
	const isPlayNowEnabled = oneBankIDFlowEnabled || oneButtonFlow;
	const registrationEnabled = !isRegistrationBlocked && !isAnonymous;
	// login is enabled for regular/full account brands or for forcing username pass or retrieve balance for SGA PnP
	const loginEnabled = hasUsernamePasswordAuthentication || bankIdEnabled;
	// reset password is only enabled for regular brands w/o play flow now where username pass is allowed
	const resetPasswordEnabled = !isPlayNowEnabled && !isAnonymous && !disableUsernamePasswordLogin;
	// isAnonymous is true for all PnP brands and showLoginAsGuestOption is true for Hybrid brands like ComeOn FI
	const guestDepositEnabled = isAnonymous || showLoginAsGuestOption;
	// guest deposit login enabled where we need login with trustly
	const guestDepositLoginEnabled = (guestDepositEnabled && !bankIdEnabled) || useTrustlyLogin;

	return (
		<>
			<SidebarCommon />
			<ContextAwareRoutes>
				<Route
					path={`${ROUTE_ACCOUNT}/*`}
					element={
						<ProtectedRoute>
							<AccountGateway />
						</ProtectedRoute>
					}
				/>
				<Route
					path={`${ROUTE_PAYMENT_RESULT}/:type?`}
					element={
						<ProtectedRoute>
							<PaymentResult />
						</ProtectedRoute>
					}
				/>
				<Route path={ROUTE_AUTHENTICATION} element={<Loading />} />
				{loginEnabled && <Route path={`${ROUTE_LOGIN}/:type?`} element={<Login />} />}
				{registrationEnabled && <Route path={ROUTE_REGISTER} element={<Register />} />}
				{isPlayNowEnabled && <Route path={`${ROUTE_PLAY_NOW}/:step?`} element={<PlaynowGateway />} />}
				<Route
					path={ROUTE_PLAY_AS_GUEST}
					element={<Navigate to={ROUTE_LOGIN_GUEST} />} // old urls need to support these
				/>
				{hasAuthResGaming && (
					<Route
						path={ROUTE_AUTH_RES_GAMING}
						element={<ResponsibleGamingLimits />} // this route is added separately since session is set on component mount and does not belong in session criteria
					/>
				)}
				<Route path={ROUTE_EXTERNAL_REQUEST} element={<ExternalIframeRequest />} />
				<Route path={ROUTE_EXTERNAL_LOGIN} element={<ExternalLogin />} />
				<Route path={ROUTE_WELCOME} element={<WelcomeScreen />} />
				<Route path={ROUTE_SUPPORT} element={<Support />} />
				<Route path={ROUTE_TERMS_CONDITIONS_SIDEBAR} element={<TermsAndConditions />} />
				<Route path={ROUTE_TERMS_CONDITIONS_BONUS_SIDEBAR} element={<TermsConditionsBonus />} />
				<Route path={ROUTE_PRIVACY_POLICY_SIDEBAR} element={<PrivacyPolicy />} />
				<Route path={ROUTE_COOKIE_POLICY_SIDEBAR} element={<CookiePolicy />} />
				<Route path={ROUTE_POINTS_FAQ} element={<ShopPointsFaq />} />
				<Route path={`${ROUTE_TOURNAMENT_LOBBY}/:id?/:tab?`} element={<TournamentLobbyLoader />} />
				<Route path={`${ROUTE_TOURNAMENT_RESULTS}/:id?`} element={<TournamentResultsLoader />} />
				<Route path={ROUTE_ENDED_TOURNAMENTS} element={<EndedTournamentListLoader />} />
				{/* TEMPORARY WESPIN FLOWS */}
				{hasWeSpin && !weSpinNewFlowEnabled && (
					<Route
						exact
						path={`${ROUTE_WE_SPIN_OLDFLOW_GAME_ROOM_ABS}/*`}
						element={<WeSpinOldFlowGameRoomRoutes />}
					/>
				)}
				{hasWeSpin && weSpinNewFlowEnabled && (
					<Route
						exact
						path={`${ROUTE_WE_SPIN_NEWFLOW_GAME_ROOM_ABS}/*`}
						element={<WeSpinNewFlowGameRoomRoutes />}
					/>
				)}
				{resetPasswordEnabled &&
					Object.entries(RESET_PASSWORD_ROUTES_MAP).map(([route, Component]) => (
						<Route key={route} path={`/${route}`} element={<Component showHeader />} />
					))}
				{guestDepositEnabled &&
					Object.entries(GUEST_DEPOSIT_LOGIN_ROUTES_MAP).map(([route, Component]) => (
						<Route key={route} path={`/${route}`} element={<Component />} />
					))}
				{/* only enable this route where /login is through trustly */}
				{guestDepositLoginEnabled && <Route path={ROUTE_GUEST_LOGIN_VERIFY_ACCOUNT} element={<GuestLogin />} />}
				{/* doesnt match anything, close the sidebar */}
				<Route path="*" element={<CloseContext />} />
			</ContextAwareRoutes>
		</>
	);
};

export default customDesktopScroll(RoutesSideBar, 'sidebar-custom-scroll');
